import { Children, isValidElement, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { last, upperFirst } from 'es-toolkit/compat';
import Fuse from 'fuse.js';
import useDocumentTitle from '@rio-cloud/rio-uikit/useDocumentTitle';
import { isMobile, isMac } from '@rio-cloud/rio-uikit/DeviceUtils';
import type { ListMenuItem, ListMenuNavItem } from '@rio-cloud/rio-uikit/components/listMenu/ListMenuGroup';
import ListMenu from '@rio-cloud/rio-uikit/ListMenu';

import { useUikitContext } from '../UikitContext';
import { routes } from '../routes';

const TITLE_PREFIX = 'RIO UIKIT - ';

type SearchableMenuNavItem = ListMenuNavItem & {
    meta?: string;
};

type SearchableMenuGroup = ListMenuItem<SearchableMenuNavItem>;

type SearchableMenuEntry = {
    groupIndex: number;
    label: string;
    key: string;
    meta: string;
    group: SearchableMenuGroup;
    navItem: SearchableMenuNavItem;
};

const MENU_FUSE_OPTIONS: Fuse.IFuseOptions<SearchableMenuEntry> = {
    ignoreLocation: true,
    includeMatches: true,
    minMatchCharLength: 2,
    threshold: 0.35,
    keys: [
        { name: 'label', weight: 0.5 },
        { name: 'key', weight: 0.35 },
        { name: 'meta', weight: 0.25 },
    ],
};

// change document title for active page
const useTitle = (pathname: string) => {
    const title = last(pathname.split('/')) ?? '';
    useDocumentTitle(`${TITLE_PREFIX}${upperFirst(title)}`);
};

const withoutMenuBadge = (menu: ListMenuItem[]) => menu.map(item => ({ ...item, badge: undefined }));

const normalizeText = (value: string) => value.replace(/\s+/g, ' ').trim();

const extractText = (node: ReactNode): string =>
    normalizeText(
        Children.toArray(node)
            .map(child => {
                if (typeof child === 'string' || typeof child === 'number') {
                    return String(child);
                }

                if (isValidElement<{ children?: ReactNode }>(child)) {
                    return extractText(child.props.children);
                }

                return '';
            })
            .join(' ')
    );

const isWordBoundary = (value: string, index: number) => {
    if (index <= 0) {
        return true;
    }

    const previousChar = value[index - 1] ?? '';
    const currentChar = value[index] ?? '';

    return !/[a-z0-9]/i.test(previousChar) || (/[a-z]/.test(previousChar) && /[A-Z]/.test(currentChar));
};

const hasStrictFuzzyMatch = (matches: readonly Fuse.FuseResultMatch[] | undefined, query: string) => {
    if (!matches?.length) {
        return false;
    }

    const minimumMatchLength = Math.max(2, query.length - 1);

    return matches.some(match =>
        match.indices.some(
            ([start, end]) => end - start + 1 >= minimumMatchLength && isWordBoundary(match.value, start)
        )
    );
};

const flattenMenuItems = (items: SearchableMenuGroup[]): SearchableMenuEntry[] =>
    items.flatMap((group, groupIndex) =>
        group.navItems.map(navItem => ({
            groupIndex,
            label: extractText(navItem.item),
            key: navItem.key,
            meta: String(navItem.meta ?? ''),
            group,
            navItem,
        }))
    );

const filterMenuItemsWithFuse = (
    items: SearchableMenuGroup[],
    value: string,
    _filterKey: keyof SearchableMenuNavItem
): SearchableMenuGroup[] => {
    const query = value.trim();

    if (!query) {
        return items;
    }

    const groupedResults = new Map<number, SearchableMenuGroup>();
    const fuse = new Fuse(flattenMenuItems(items), MENU_FUSE_OPTIONS);

    for (const result of fuse.search(query)) {
        if (!hasStrictFuzzyMatch(result.matches, query)) {
            continue;
        }

        const { item } = result;
        const existingGroup = groupedResults.get(item.groupIndex);

        if (existingGroup) {
            existingGroup.navItems.push(item.navItem);
            continue;
        }

        groupedResults.set(item.groupIndex, {
            ...item.group,
            navItems: [item.navItem],
        });
    }

    return [...groupedResults.values()];
};

export const UikitMenu = () => {
    const [filterValue, setFilterValue] = useState('');
    const showInternalItemsInGlobalSearch = import.meta.env.DEV;

    const { pathname } = useLocation();

    const { startMenu, foundationsMenu, componentsMenu, uiTemplateMenu, utilitiesMenu, internalMenu } =
        useUikitContext();

    useTitle(pathname);

    const isDesktop = !isMobile();

    const isMacOs = isMac();

    const items = useMemo<SearchableMenuGroup[]>(() => {
        // No filter: select menu based on pathname
        if (!filterValue) {
            if (pathname.startsWith(`/${routes.START}`)) {
                return withoutMenuBadge(startMenu) as SearchableMenuGroup[];
            }
            if (pathname.startsWith(`/${routes.COMPONENTS}`)) {
                return withoutMenuBadge(componentsMenu) as SearchableMenuGroup[];
            }
            if (pathname.startsWith(`/${routes.FOUNDATIONS}`)) {
                return withoutMenuBadge(foundationsMenu) as SearchableMenuGroup[];
            }
            if (pathname.startsWith(`/${routes.TEMPLATES}`)) {
                return withoutMenuBadge(uiTemplateMenu) as SearchableMenuGroup[];
            }
            if (pathname.startsWith(`/${routes.UTILITIES}`)) {
                return withoutMenuBadge(utilitiesMenu) as SearchableMenuGroup[];
            }
            if (pathname.startsWith(`/${routes.INTERNAL}`)) {
                return withoutMenuBadge(internalMenu) as SearchableMenuGroup[];
            }
            return [];
        }

        // With filter: merge all menu items
        return [
            ...startMenu,
            ...foundationsMenu,
            ...componentsMenu,
            ...uiTemplateMenu,
            ...utilitiesMenu,
            ...(showInternalItemsInGlobalSearch ? internalMenu : []),
        ] as SearchableMenuGroup[];
    }, [
        filterValue,
        pathname,
        startMenu,
        foundationsMenu,
        componentsMenu,
        uiTemplateMenu,
        utilitiesMenu,
        internalMenu,
        showInternalItemsInGlobalSearch,
    ]);

    return (
        <ListMenu
            className='padding-bottom-25'
            menuItems={items}
            enableFilter
            focusFilter
            filterFn={filterMenuItemsWithFuse}
            filterKey='meta'
            filterPlaceholder='Filter by name'
            onFilterChange={setFilterValue}
            notFoundMessage='Nothing found'
            responsive={false}
            trailingInputAddon={
                isDesktop && (
                    <div className='input-group-addon padding-right-5'>
                        <kbd className='text-normal'>{isMacOs ? '⌘' : 'ctrl'}</kbd>
                        <kbd className='text-normal'>k</kbd>
                    </div>
                )
            }
        />
    );
};
